import React from 'react';
import { Link as BaseLink } from 'react-router5';
import { DecodedRouteParams, NavigateToFn, Route, State as RouteState } from 'config/routes';
import { BaseLinkProps } from 'react-router5/types/BaseLink';
import { Omit } from 'utility-types';
import { noop } from 'lodash';


export interface LinkProps extends Omit<BaseLinkProps, 'router'> {
  routeName: Route;
  routeParams?: Partial<DecodedRouteParams>;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  route: RouteState;
  preserveParams?: boolean;
  hideLinkParams?: boolean;
  rel?: string;
  navigate: NavigateToFn;
}

const ROUTE_WITH_HIDDEN_PARAMS = new Set([
  Route.UnitPage,
  Route.CommercialMarketLanding,
  Route.UnitPageCommercial,
  Route.SearchCommercial,
  Route.Search,
  Route.CheckAddress,
  Route.DevelopersSearchPage,
  Route.MadadPage,
  Route.MadadAgentPage,
  Route.MadadArchivePage,
  Route.UploadBulletinForm,
  Route.UploadBulletin,
  Route.ProjectPage,
  Route.DeveloperPage,
  Route.LocalPage,
  Route.AgentPage,
  Route.MortgageOfficePage,
  Route.OfficePage,
  Route.Accessibility,
  Route.DevelopersPromotionPage,
  Route.AgentsPromotionPage,
  Route.AgentConsoleHomePage,
  Route.ManageBulletins,
  Route.Home,
  Route.TermsPage,
  Route.ProjectPageCommercial,
  Route.StreetPage,
  Route.AddressPage,
  Route.EmploymentAreaPage,
  Route.UploadBulletin,
  Route.UploadBulletinForm,
  Route.MortgageOfficesSearchPage,
]);

export const  DumbLink: React.FC<LinkProps> = ({
  preserveParams,
  route,
  routeName,
  routeParams = {},
  children,
  onClick = noop,
  hideLinkParams,
  navigate,
  onTransitionEndCapture,
  ...rest
}) => {
  const nextParams: Partial<DecodedRouteParams> = preserveParams
    ? {
      ...route.params,
      insightId: undefined,
      insightCollection: undefined,
      ...routeParams,
    }
    : routeParams;

  if (route.name === Route.Search && route.params.dealType && nextParams.dealType && !nextParams.tracking_search_source) {
    nextParams.tracking_search_source = 'type_change';
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onClick(e);
    navigate(routeName, nextParams, { openInNewTab: rest.target === '_blank' });

    return false;
  };

  const htmlRouteParamsByRouteName = React.useMemo(() => {
    if (!hideLinkParams) return nextParams;

    switch (routeName) {
      case Route.Search:
      case Route.SearchCommercial:
        return {
          dealType: nextParams.dealType,
          term: nextParams.term,
        };
      case Route.UnitPage:
      case Route.UnitPageCommercial:
      case Route.ProjectPage:
      case Route.ProjectPageCommercial:
      case Route.DeveloperPage:
      case Route.LocalPage:
      case Route.AgentPage:
      case Route.MortgageOfficePage:
      case Route.OfficePage:
      case Route.StreetPage:
        return {
          id: nextParams.id,
        };
      case Route.CommercialMarketLanding:
      case Route.CheckAddress:
      case Route.DevelopersSearchPage:
      case Route.MadadPage:
      case Route.MadadAgentPage:
      case Route.UploadBulletinForm:
      case Route.UploadBulletin:
      case Route.DevelopersPromotionPage:
      case Route.AgentsPromotionPage:
      case Route.Accessibility:
      case Route.AgentConsoleHomePage:
      case Route.ManageBulletins:
      case Route.Home:
      case Route.TermsPage:
      case Route.MortgageOfficesSearchPage:
        return {};
      case Route.MadadArchivePage:
        return {
          year: nextParams.year,
        };
      case Route.AddressPage:
        return {
          address: nextParams.address,
        };
      default:
        return nextParams;
    }
  }, [ hideLinkParams, nextParams, routeName ]);

  const clickHandlerResolver = ROUTE_WITH_HIDDEN_PARAMS.has(routeName) && hideLinkParams ? handleClick : onClick;

  return (
    <BaseLink
      {...rest}
      routeName={routeName}
      routeParams={htmlRouteParamsByRouteName}
      onClick={clickHandlerResolver}
    >
      {children}
    </BaseLink>
  );
};
